import * as v from 'valibot'
import * as R from 'remeda'
import { useMemo } from 'react'
import {
  AcceptedInput,
  Content,
  ConversationAction,
  PiperAiContent,
} from '@chilipiper/api-client/src/chat-platform-chat'
import {
  Block,
  BookMeetingBlock,
  EventsMap,
  FillFormBlock,
  MessageBlock,
  ParsedBlock,
  ParsedPayloadIn,
  ReplyButtonsBlock,
  PiperAiMessageBlock,
} from './types'

const unescape = (text: string) => text.replace(/\\n/g, `\n`).replace(/\\t/g, `\t`)
const contentHandler = (
  message: Content | PiperAiContent,
  author: string,
  event: Extract<ParsedPayloadIn, { type: 'ConversationData' }>,
  index: number
): MessageBlock | PiperAiMessageBlock => {
  return {
    id: `${event.id}___content_${index}`,
    author,
    sent: true,
    type: message.type === 'PiperAiContent' ? 'PiperAiMessage' : 'Message',
    parts: message.value.map(part =>
      part.type === 'Text' ? { type: 'Text', text: unescape(part.text).trim() } : part
    ),
    statuses: event.statuses,
    timestamp: event.timestamp,
    roomId: event.roomId,
  }
}

/*eslint complexity: ["error", 24]*/
const acceptedInputHandler = (
  message: AcceptedInput,
  currBlocks: Array<ParsedBlock>
): Array<ParsedBlock> => {
  const messageValue = message.value
  switch (messageValue.type) {
    case 'ReplyButtonSelected': {
      const lastReplyButtonsIndex =
        R.findLastIndex(currBlocks, block => block.type === 'ReplyButtons') ?? -1
      const repliesBlock = currBlocks[lastReplyButtonsIndex] as ReplyButtonsBlock

      if (!repliesBlock) {
        return currBlocks
      }

      const newBlock: ReplyButtonsBlock = {
        ...repliesBlock,
        selected: messageValue.index,
      }

      return currBlocks.map((block, index) => {
        if (index === lastReplyButtonsIndex) {
          return newBlock
        }
        return block
      })
    }
    case 'FormData': {
      const lastFormIndex = R.findLastIndex(currBlocks, block => block.type === 'FillForm') ?? -1
      const formDataBlock = currBlocks[lastFormIndex] as FillFormBlock

      if (!formDataBlock) {
        return currBlocks
      }

      const newBlock: FillFormBlock = {
        ...formDataBlock,
        values: messageValue.values,
      }

      return currBlocks.map((block, index) => {
        if (index === lastFormIndex) {
          return newBlock
        }
        return block
      })
    }
    case 'MeetingSlotSelectedWithReservation':
    case 'MeetingSlotSelectedWithoutReservation': {
      const lastMeetingIndex =
        R.findLastIndex(currBlocks, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = currBlocks[lastMeetingIndex] as BookMeetingBlock

      if (!meetingSlotBlock) {
        return currBlocks
      }

      const newBlock: BookMeetingBlock = {
        ...meetingSlotBlock,
        email: messageValue.email,
      }

      return currBlocks.map((block, index) => {
        if (index === lastMeetingIndex) {
          return newBlock
        }
        return block
      })
    }

    case 'MeetingSlotSelectionCancelled': {
      const lastMeetingIndex =
        R.findLastIndex(currBlocks, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = currBlocks[lastMeetingIndex] as BookMeetingBlock

      if (!meetingSlotBlock) {
        return currBlocks
      }

      const newBlock: BookMeetingBlock = {
        ...meetingSlotBlock,
        cancelled: true,
      }

      return currBlocks.map((block, index) => {
        if (index === lastMeetingIndex) {
          return newBlock
        }
        return block
      })
    }

    case 'MessageToPiperAi':
    case 'AiResponse':
    case 'RepSentMessage':
    case 'Start':
    case 'WaitingForRepHasTimedout':
    case 'NodeExecutionTimedout':
    case 'NodeExecutionResumed': {
      return currBlocks
    }

    default:
      messageValue satisfies never
      return currBlocks
  }
}

const conversationDataActionHandler = (
  action: ConversationAction,
  guestId: string | undefined,
  botId: string,
  event: Extract<ParsedPayloadIn, { type: 'ConversationData' }>
): Block | undefined => {
  const guest = guestId ?? 'guest'

  const commonData = {
    sent: true,
    id: `${event.id}___action`,
    author: guest,
    statuses: event.statuses,
    timestamp: event.timestamp,
    roomId: event.roomId,
  } satisfies Partial<Block>

  switch (action.type) {
    case 'SelectReplyAction': {
      return {
        ...commonData,
        type: 'ReplyButtons',
        replies: action.replies,
      }
    }

    case 'SubmitFormAction': {
      return {
        ...commonData,
        type: 'FillForm',
        fields: action.fields,
      }
    }

    case 'RepJoinOrTimeoutAction':
    case 'StartLiveChat': {
      return {
        ...commonData,
        author: botId,
        type: 'RouteToRep',
      }
    }

    case 'TerminateConversation': {
      return {
        ...commonData,
        author: botId,
        type: 'TerminateConversation',
      }
    }

    case 'SelectMeetingSlotAction': {
      return {
        ...commonData,
        distributionId:
          action.model.assignee.type === 'Team' ? action.model.assignee.distributionId : undefined,
        type: 'BookMeeting',
        assignees: action.assignees,
        meetingTypeId: action.model.meetingType.id,
        email: action.email,
        timeout: parseTimeout(action.model.slotSelectionTimeout),
      }
    }

    case 'EnterPiperAiChat': {
      return {
        ...commonData,
        type: 'EnterPiperAiChat',
      }
    }

    case 'ResumeNodeExecution':
    case 'SelectDataFieldPicklistReplyAction':
    case 'SubmitFormActionError':
    case 'SubmitFormEntry': {
      return undefined
    }

    default: {
      action satisfies never
    }
  }
}

const TimeoutSchema = v.object({
  number: v.pipe(v.number(), v.minValue(0)),
  unit: v.picklist(['minutes', 'seconds', 'minute', 'second']),
})

/**
 * Parses a timeout string into milliseconds
 *
 * Supports only "minutes", "minute", "seconds" and "second" units
 *
 * @example
 *   parseTimeout('10 minutes') // 600000
 *   parseTimeout('1 second') // 1000
 */
const parseTimeout = (timeout?: string) => {
  const match = timeout?.match(/(\d+)\s*(\w+)/)

  if (match) {
    const { success, output } = v.safeParse(TimeoutSchema, {
      number: parseInt(match[1] as string, 10),
      unit: match[2],
    })
    if (success) {
      if (output.unit === 'seconds' || output.unit === 'second') {
        return output.number * 1000
      }
      if (output.unit === 'minutes' || output.unit === 'minute') {
        return output.number * 60 * 1000
      }
    }
  }
}

/**
 * Consider that a ConversationData event may contain messages, a request for user input and also
 * previous user input in case user replied to a block. This function will break them down into
 * individual blocks.
 */
const assembleBlocks = (events: ParsedPayloadIn[], guestId: string | undefined, botId: string) => {
  let parsedEvents: Array<ParsedBlock> = []

  // eslint-disable-next-line complexity
  events.forEach(event => {
    if (event.type === 'ConversationData') {
      event.messages?.forEach((message, index) => {
        if (message.type === 'Content' || message.type === 'PiperAiContent') {
          const contentBlock = contentHandler(message, botId, event, index)
          parsedEvents.push(contentBlock)
        }

        if (message.type === 'AcceptedInput') {
          parsedEvents = acceptedInputHandler(message, parsedEvents)
        }
      })

      const actionBlock = conversationDataActionHandler(event.action, guestId, botId, event)

      if (actionBlock) {
        parsedEvents.push(actionBlock)
      }
    } else if (
      event.type === 'SystemMessage' &&
      event.payload?.type === 'MeetingSchedulingRequested'
    ) {
      const lastMeetingIndex =
        R.findLastIndex(parsedEvents, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = parsedEvents[lastMeetingIndex] as BookMeetingBlock

      if (meetingSlotBlock) {
        const newBlock: BookMeetingBlock = {
          ...meetingSlotBlock,
          meetingId: event.payload.meetingId,
        }

        parsedEvents = parsedEvents.map((block, index) => {
          if (index === lastMeetingIndex) {
            return newBlock
          }
          return block
        })
      }
    } else {
      if (
        event.type === 'ConversationSessionExpired' ||
        event.type === 'DecodingFailure' ||
        event.type === 'MeetingSchedulingFailed' ||
        event.type === 'MeetingSchedulingRequested' ||
        event.type === 'SystemConversationAssigned' ||
        event.type === 'SystemConversationReassigned' ||
        event.type === 'SystemRepsHaveFailedToRespond' ||
        event.type === 'SystemConversationReassignedFromTeam' ||
        event.type === 'FormData' ||
        event.type === 'MeetingSlotSelectedWithoutReservation' ||
        event.type === 'MeetingSlotSelectedWithReservation' ||
        event.type === 'MeetingSlotSelectionCancelled' ||
        event.type === 'NodeExecutionResumed' ||
        event.type === 'NodeExecutionTimedout' ||
        event.type === 'RepSentMessage' ||
        event.type === 'ReplyButtonSelected' ||
        event.type === 'Start' ||
        event.type === 'WaitingForRepHasTimedout' ||
        event.type === 'InternalServerError' ||
        event.type === 'SystemConversationDeadline' ||
        event.type === 'SetupPiperAiChat' ||
        event.type === 'AiResponse'
      ) {
        return
      }
      parsedEvents.push(event)
    }
  })

  return parsedEvents
}

/**
 * Based on marker eventId, this function will apply its status into the correspondent events. Then,
 * it will remove the marker from the list of events.
 */
const applyMarkers = (events: EventsMap) => {
  const { marker = [], other = [] } = R.groupBy(Object.values(events), event =>
    event?.type === 'Marker' ? 'marker' : 'other'
  )

  const latestMarkers = marker.reduce<
    Record<
      string,
      { authorId: string; roomId: string; status: 'displayed' | 'failed'; timestamp: string }
    >
  >((map, event) => {
    if (!event?.id || event.type !== 'Marker') {
      return map
    }
    const key = `${event.roomId}_${event.author}_${event.status}`
    const existingMarker = map[key]
    if (!existingMarker || existingMarker.timestamp < event.timestamp) {
      map[key] = {
        authorId: event.author,
        roomId: event.roomId,
        timestamp: event.timestamp,
        status: event.status,
      }
    }
    return map
  }, {})

  const latestMarkersByRoom = R.groupBy(R.values(latestMarkers), marker => marker.roomId)

  return other.reduce<Record<string, ParsedPayloadIn>>((acc, event) => {
    if (!event?.id || event.type === 'Marker') {
      return acc
    }
    const markers = latestMarkersByRoom[event.roomId] ?? []
    if (markers.length === 0) {
      acc[event.id] = event
      return acc
    }

    acc[event.id] = { ...event }

    const eventToModify = acc[event.id]
    for (const marker of markers) {
      if (event.timestamp <= marker.timestamp && eventToModify) {
        eventToModify.statuses = {
          ...eventToModify.statuses,
          [marker.authorId]: marker.status,
        }
      }
    }
    return acc
  }, {})
}

/** Parses chat markers into event status and ConversationData into blocks */
export const useAssembledBlocksAndMarkers = (
  events: EventsMap | undefined,
  guestId: string | undefined,
  botId: string | undefined
) => {
  const eventsArrayWithAppliedMarkers = useMemo(() => {
    if (!events) {
      return []
    }
    const result = Object.values(applyMarkers(events) ?? {})
    // Removes user message accepting input (duplicated in mongoose)
    // We need to sort from oldest to newest because AcceptedInput events need to come after the input they are accepting
    return result.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  }, [events])

  const assembledBlocks = useMemo(() => {
    if (!botId) {
      return []
    }
    return assembleBlocks(eventsArrayWithAppliedMarkers, guestId, botId)
  }, [eventsArrayWithAppliedMarkers, guestId, botId])

  return assembledBlocks
}
